import { useState, useCallback } from 'react';
import { 
  downloadAttachment, 
  deleteAttachment,
  restoreVersion,
} from '../../../../../services/attachmentService';

/**
 * useAttachmentsActions Hook
 * 
 * Handles attachment actions:
 * - Download (file or specific version)
 * - Delete (single version or all versions)
 * - Preview and compare
 * - Restore version
 * - Delete confirmation modal state
 */
const useAttachmentsActions = ({
  taskId,
  attachments, // Current attachments array for count calculation
  onAttachmentsChange,
  removeAttachment,
  removeVersion,
  updateStorageUsed,
  setError,
  loadAttachments,
}) => {
  // Preview state
  const [previewFile, setPreviewFile] = useState(null);
  const [compareVersion, setCompareVersion] = useState(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ 
    isOpen: false, 
    file: null, 
    versionId: null 
  });

  // Download file or version
  const handleDownload = useCallback(async (file, versionId = null) => {
    try {
      const attachmentId = versionId || file.id;
      await downloadAttachment(taskId, attachmentId);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download file');
    }
  }, [taskId, setError]);

  // Open delete confirmation modal
  const handleDeleteRequest = useCallback((file, versionId = null) => {
    setDeleteConfirm({ isOpen: true, file, versionId });
  }, []);

  // Close delete confirmation modal
  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirm({ isOpen: false, file: null, versionId: null });
  }, []);

  // Perform single version/file delete
  const performDelete = useCallback(async (file, versionId = null) => {
    const attachmentId = versionId || file.id;
    const fileSize = versionId 
      ? file.versions?.find(v => v.id === versionId)?.size || 0
      : file.size;
    
    try {
      await deleteAttachment(taskId, attachmentId);
      
      // Update local state
      if (versionId && file.versions?.length > 1) {
        removeVersion(file.id, versionId, fileSize);
        // Version deleted but file still exists, count unchanged
        onAttachmentsChange?.(attachments.length);
      } else {
        removeAttachment(file.id);
        updateStorageUsed(-fileSize);
        // File deleted, count decreases by 1
        onAttachmentsChange?.(attachments.length - 1);
      }
      
      return true;
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete file');
      return false;
    }
  }, [taskId, removeAttachment, removeVersion, updateStorageUsed, onAttachmentsChange, setError]);

  // Delete all versions of a file
  const performDeleteAll = useCallback(async (file) => {
    if (!file.versions || file.versions.length === 0) {
      return performDelete(file);
    }

    let totalDeleted = 0;
    
    // Delete all versions
    for (const version of file.versions) {
      try {
        await deleteAttachment(taskId, version.id);
        totalDeleted += version.size;
      } catch (err) {
        console.error('Failed to delete version:', version.id, err);
      }
    }

    // Remove from local state
    removeAttachment(file.id);
    updateStorageUsed(-totalDeleted);
    
    // Notify parent with new count (file deleted)
    onAttachmentsChange?.(attachments.length - 1);
  }, [taskId, performDelete, removeAttachment, updateStorageUsed, onAttachmentsChange, attachments.length]);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    const { file, versionId } = deleteConfirm;
    closeDeleteConfirm();
    await performDelete(file, versionId);
  }, [deleteConfirm, closeDeleteConfirm, performDelete]);

  // Handle delete all confirmation
  const handleDeleteAllConfirm = useCallback(async () => {
    const { file } = deleteConfirm;
    closeDeleteConfirm();
    await performDeleteAll(file);
  }, [deleteConfirm, closeDeleteConfirm, performDeleteAll]);

  // Preview handlers
  const handlePreview = useCallback((file) => {
    setPreviewFile(file);
    setCompareVersion(null);
  }, []);

  const handleCompare = useCallback((file, version) => {
    setPreviewFile(file);
    setCompareVersion(version);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewFile(null);
    setCompareVersion(null);
  }, []);

  // Restore version (with optional note)
  const handleRestore = useCallback(async (file, version, note = null) => {
    try {
      await restoreVersion(taskId, version.id, note);
      
      // Reload to get updated version list
      await loadAttachments();
      
      // Notify parent (restore doesn't change file count, just versions)
      onAttachmentsChange?.(attachments.length);
    } catch (err) {
      console.error('Restore failed:', err);
      setError('Failed to restore version.');
    }
  }, [taskId, loadAttachments, onAttachmentsChange, setError, attachments.length]);

  return {
    // Preview state
    previewFile,
    compareVersion,
    handlePreview,
    handleCompare,
    closePreview,
    
    // Delete state
    deleteConfirm,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteAllConfirm,
    closeDeleteConfirm,
    
    // Actions
    handleDownload,
    handleRestore,
  };
};

export default useAttachmentsActions;
